import { isAxiosError, isCancel } from 'axios';
import type { ErrorBody } from './types';

export type TransportKind = 'http' | 'network' | 'cancel' | 'unknown';

/**
 * API 오류를 화면에서 쓰기 쉬운 형태로 바꾼다 (crm-client AppError 계승).
 * - message: 서버 오류 키 (예: NOT_FOUND_AREA) — 분기용
 * - translate: 요청 언어로 번역된 문구 — 표시용
 */
export default class AppError extends Error {
  readonly kind: TransportKind;
  readonly statusCode?: number;
  readonly key?: string;
  readonly translate?: string;
  readonly data?: unknown;
  readonly traceId?: string;
  readonly isTimeout: boolean;
  readonly retryAfterSeconds?: number;

  constructor(cause: unknown) {
    const body = isAxiosError(cause)
      ? (cause.response?.data as Partial<ErrorBody> | undefined)
      : undefined;
    const key = typeof body?.message === 'string' ? body.message : undefined;
    super(key ?? (cause instanceof Error ? cause.message : String(cause)));
    this.name = 'AppError';

    if (isCancel(cause)) {
      this.kind = 'cancel';
    } else if (isAxiosError(cause) && cause.response) {
      this.kind = 'http';
    } else if (isAxiosError(cause)) {
      this.kind = 'network';
    } else {
      this.kind = 'unknown';
    }

    const status =
      body?.statusCode ??
      (isAxiosError(cause) ? cause.response?.status : undefined);
    this.statusCode = typeof status === 'number' ? status : undefined;
    this.key = key;
    this.translate =
      typeof body?.translate === 'string' ? body.translate : undefined;
    this.data = body?.data;
    this.traceId = typeof body?.traceId === 'string' ? body.traceId : undefined;
    this.isTimeout =
      isAxiosError(cause) &&
      ['ECONNABORTED', 'ETIMEDOUT'].includes(cause.code ?? '');
    const retryAfter = isAxiosError(cause)
      ? cause.response?.headers['retry-after']
      : undefined;
    if (typeof retryAfter === 'string' && retryAfter.trim()) {
      const seconds = /^\d+$/.test(retryAfter)
        ? Number(retryAfter)
        : Math.ceil((Date.parse(retryAfter) - Date.now()) / 1000);
      if (Number.isFinite(seconds) && seconds > 0)
        this.retryAfterSeconds = Math.min(seconds, 86400);
    }
  }

  /** 사용자에게 보여줄 문구: 서버 번역 → 기본 문구 */
  displayMessage(fallback: string) {
    return this.translate || fallback;
  }
}
